<template>
    <div class="advisor-variants">
        <label
            v-for="(variant, index) in variants"
            :key="index"
            class="advisor-variants__item"
            :class="{
                'advisor-variants__item--danger': variant.destructive,
                'advisor-variants__item--selected': selected === index,
            }"
        >
            <input v-model="selected" type="radio" class="advisor-variants__radio" :value="index" />
            <span class="advisor-variants__head">
                <span class="advisor-variants__label">{{ variant.label }}</span>
                <span v-if="variant.destructive" class="advisor-variants__tag">
                    {{ lang('advisor::advisor.irreversible') }}
                </span>
            </span>
            <ul v-if="variant.shifts?.length" class="advisor-variants__rows">
                <li v-for="(shift, si) in variant.shifts" :key="si" class="advisor-variants__row">
                    <span>{{ formatDate(shift.date) }}<template v-if="shift.workplace"> · {{ shift.workplace }}</template></span>
                    <span class="advisor-variants__row-val">{{ shift.time }}</span>
                </li>
            </ul>
            <div v-for="(warning, wi) in variant.warnings ?? []" :key="wi" class="advisor-variants__warning">
                {{ warning.message }}
            </div>
        </label>

        <div v-if="!done && !readonly" class="advisor-variants__actions">
            <Button
                size="sm"
                :variant="selectedVariant?.destructive ? 'danger' : undefined"
                :loading="committing"
                :disabled="selected === null || committing"
                @click="confirm"
            >
                {{ lang('advisor::advisor.preview.confirm') }}
            </Button>
            <Button size="sm" variant="secondary" :disabled="committing" @click="$emit('cancel')">
                {{ lang('advisor::advisor.preview.cancel') }}
            </Button>
        </div>

        <div v-if="status" class="advisor-variants__status" :class="{ 'advisor-variants__status--error': error }">
            {{ status }}
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { lang } from '@/lang/composable';
import { useAdvisorDrawer } from '@src/Advisor/Pages/composables/useAdvisorDrawer';
import type { Grounded, PreviewVariant } from '@src/Advisor/Pages/types';

const props = defineProps<{ g: Grounded; readonly?: boolean }>();

defineEmits<{ cancel: [] }>();

const drawer = useAdvisorDrawer();

const variants = computed<PreviewVariant[]>(() => props.g.variants ?? []);

// Pre-select the first non-destructive variant (never default to a delete);
// if every option is destructive, leave unselected so the user picks explicitly.
const firstSafe = variants.value.findIndex((v) => !v.destructive);
const selected = ref<number | null>(firstSafe >= 0 ? firstSafe : null);

const selectedVariant = computed(() => (selected.value === null ? null : variants.value[selected.value]));

const committing = ref(false);
const done = ref(false);
const error = ref(false);
const status = ref('');

const formatDate = (value: string): string => {
    const d = new Date(`${value}T00:00:00`);

    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });
};

const confirm = (): void => {
    const variant = selectedVariant.value;

    if (!variant || !props.g.skill || committing.value) {
        return;
    }

    committing.value = true;
    error.value = false;
    status.value = '';

    drawer
        .commit(props.g.skill, variant.commit)
        .then(() => {
            done.value = true;
            status.value = lang('advisor::advisor.preview.success');
        })
        .catch((err) => {
            error.value = true;
            status.value = err?.response?.data?.error ?? lang('advisor::advisor.preview.error');
        })
        .finally(() => {
            committing.value = false;
        });
};
</script>

<style scoped>
.advisor-variants {
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.advisor-variants__item {
    display: block;
    padding: 0.6rem 0.75rem;
    border: 1px solid rgb(0 0 0 / 0.12);
    border-radius: 0.5rem;
    cursor: pointer;
}

.advisor-variants__item--selected {
    border-color: rgb(0 0 0 / 0.45);
}

.advisor-variants__item--danger.advisor-variants__item--selected {
    border-color: #dc2626;
}

.advisor-variants__radio {
    margin-right: 0.5rem;
}

.advisor-variants__head {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.advisor-variants__label {
    font-weight: 600;
}

.advisor-variants__tag {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #dc2626;
    border: 1px solid #dc2626;
    border-radius: 9999px;
    padding: 0 0.4rem;
}

.advisor-variants__rows {
    margin-top: 0.35rem;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    font-size: 0.875rem;
}

.advisor-variants__row {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
}

.advisor-variants__row-val {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.advisor-variants__warning {
    margin-top: 0.35rem;
    font-size: 0.8rem;
    color: #b45309;
}

.advisor-variants__actions {
    display: flex;
    gap: 0.5rem;
}

.advisor-variants__status {
    font-size: 0.875rem;
}

.advisor-variants__status--error {
    color: #dc2626;
}
</style>
